/**
 * TaskChargesTab - Aba de cobranças financeiras no modal de tarefa
 *
 * Reaproveita 100% a estrutura já usada em DealChargesTab/CustomerChargesTab:
 * mesma RPC paginada (get_cobrancas_paginated), mesma tabela (ChargesTableView)
 * e mesmo modal de criação (CreateChargeModal).
 *
 * Regras:
 *  - As cobranças são filtradas por `task_id` (vínculo gravado na criação).
 *  - Sem cliente vinculado à tarefa, a criação é bloqueada (a cobrança exige
 *    obrigatoriamente um cliente no financeiro).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-22 v1.0.0 - Criação da aba (Retenção, Jurídico e Normal)
 */
import React, { useState } from 'react';
import { useFinanceiroCobrancasPaginated } from '@/hooks/useFinanceiroCobrancasPaginated';
import { ChargesTableView } from '@/components/financial/ChargesTableView';
import { CreateChargeModal } from '@/components/financial/CreateChargeModal';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';

/** Props do componente */
interface TaskChargesTabProps {
  /** ID da tarefa (origem do vínculo financeiro) */
  taskId: string;
  /** Se a aba está ativa (lazy loading) */
  isActive?: boolean;
  /** Cliente vinculado à tarefa (obrigatório para criar cobrança) */
  customerId?: string | null;
  /** Negócio vinculado à tarefa (pré-preenche a cobrança quando existir) */
  dealId?: string | null;
  /** Permissão para criar nova cobrança (financial_charges.create) */
  canCreate?: boolean;
  /** Permissão para editar cobrança (repassada ao modal de detalhes) */
  canEdit?: boolean;
  /** Permissão para excluir/cancelar cobrança */
  canDelete?: boolean;
}

/**
 * Aba de cobranças dentro do modal de tarefa.
 * Fonte de dados unificada: RPC get_cobrancas_paginated com filtro task_id.
 */
export function TaskChargesTab({
  taskId,
  isActive = false,
  customerId,
  dealId,
  canCreate = true,
  canEdit = true,
  canDelete = true,
}: TaskChargesTabProps) {
  // Estado do modal de criação de cobrança
  const [createChargeOpen, setCreateChargeOpen] = useState(false);

  // Busca cobranças da tarefa via RPC paginada (pageSize alto: lista completa)
  const { cobrancas, isLoading } = useFinanceiroCobrancasPaginated(
    isActive ? { task_id: taskId } : undefined,
    { page: 1, pageSize: 200 }
  );

  // Sem cliente vinculado não é possível gerar cobrança no financeiro
  const podeCriar = canCreate && !!customerId;

  return (
    <div className="flex flex-col gap-3">
      {/* Aviso quando a tarefa não tem cliente vinculado */}
      {canCreate && !customerId && (
        <InlineNotice variant="warning">
          Vincule um cliente a esta tarefa para gerar cobranças financeiras.
        </InlineNotice>
      )}

      {/* Cabeçalho com botão de ação */}
      {podeCriar && (
        <div className="flex items-center justify-end">
          <Button
            size="sm"
            onClick={() => setCreateChargeOpen(true)}
            className="h-8 text-xs gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Nova Cobrança
          </Button>
        </div>
      )}

      {/* Tabela de cobranças (mesma usada em clientes e negócios) */}
      <ChargesTableView
        charges={cobrancas}
        isLoading={isLoading}
        emptyMessage="Nenhuma cobrança vinculada a esta tarefa"
        canEdit={canEdit}
        canDelete={canDelete}
        emptyAction={
          podeCriar ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreateChargeOpen(true)}
              className="text-xs gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              Criar primeira cobrança
            </Button>
          ) : undefined
        }
      />

      {/* Modal de criação com tarefa/cliente/negócio pré-preenchidos */}
      {podeCriar && (
        <CreateChargeModal
          open={createChargeOpen}
          onOpenChange={setCreateChargeOpen}
          prefillTaskId={taskId}
          prefillCustomerId={customerId}
          prefillDealId={dealId ?? undefined}
          lockDeal
        />
      )}
    </div>
  );
}
